import React from 'react';
import { useTranslation } from 'react-i18next';
import { playbookTotals, formatRange } from '../../lib/playbookEconomics';

/**
 * Grafica del SUMATORIO de la escalera de valor: una barra por playbook, en el
 * orden de la escalera (`step`), con lo que suman sus procesos.
 *
 * Barras en CSS puro (flex + alturas en %), el mismo lenguaje que el mockup de
 * dashboard de la web (`DashboardMockup`: barContainer/bar/barLabel). No entra
 * ninguna libreria de graficas: el proyecto no tiene, y para un grafico de
 * barras no compensa.
 *
 * Lo que se pinta es el RECURRENTE MENSUAL, que es lo que hace de escalera. El
 * pago unico va como marca secundaria y el "por uso" NO se dibuja: son unidades
 * distintas (por lead, por minuto...) y el backend prohibe sumarlas entre si;
 * se enseña como texto en la ficha de cada playbook.
 */
const PlaybookLadderChart = ({ playbooks, locale }) => {
   const { t } = useTranslation();

   const rows = playbooks
      .map((playbook) => {
         const totals = playbookTotals(playbook.economics);
         return {
            key: playbook.UUID || playbook.internal_name,
            title: playbook.title || playbook.internal_name,
            step: playbook.step,
            totals,
            monthlyMax: totals && totals.monthly ? totals.monthly.max : 0,
         };
      })
      // Con `totals` pero sin bucket mensual (un playbook solo por uso) no hay
      // barra que dibujar Y ADEMAS `totals.monthly` es null: si se colaba aqui,
      // el formateo de abajo reventaba la pagina entera.
      .filter((row) => row.totals && row.totals.monthly);

   // Esta grafica COMPARA playbooks entre si: con menos de dos barras no compara
   // nada y una sola barra al 100% se lee como un error. Sin importes mensuales,
   // igual. En los dos casos las cifras siguen estando en la ficha de cada
   // playbook, que es donde se leen de verdad.
   const top = Math.max(...rows.map((r) => r.monthlyMax), 0);
   if (rows.length < 2 || top <= 0) return null;

   const anyPartial = rows.some((r) => r.totals.isPartial);

   return (
      <div className="pbl-chart">
         <div className="pbl-chart__head">
            <h3>{t('pbl_chart_title')}</h3>
            <p>{t('pbl_chart_subtitle')}</p>
         </div>

         <div className="pbl-chart__plot">
            {rows.map((row) => {
               const pct = Math.max(6, Math.round((row.monthlyMax / top) * 100));
               const label = formatRange(
                  row.totals.monthly.min,
                  row.totals.monthly.max,
                  row.totals.currency,
                  locale
               );
               return (
                  <div className="pbl-chart__col" key={row.key}>
                     <span className="pbl-chart__value">
                        {label}
                        {row.totals.isPartial ? <em>+</em> : null}
                     </span>
                     <div className="pbl-chart__track">
                        {/* El valor tambien va en texto encima: la barra no es
                            el unico portador del dato. */}
                        <div
                           className="pbl-chart__bar"
                           style={{ height: `${pct}%` }}
                           role="img"
                           aria-label={`${row.title}: ${label} ${t('pbl_per_month')}`}
                        />
                     </div>
                     <span className="pbl-chart__step">{t('pbl_step', { n: row.step })}</span>
                     <span className="pbl-chart__name">{row.title}</span>
                  </div>
               );
            })}
         </div>

         <p className="pbl-chart__foot">
            {t('pbl_chart_foot')}
            {anyPartial ? ` ${t('pbl_chart_partial')}` : ''}
         </p>
      </div>
   );
};

export default PlaybookLadderChart;
